'use client';

import { memo, useRef } from 'react';
import Link from 'next/link';
import { ErrorBoundary } from 'react-error-boundary';
import classNames from 'classnames';

import useMasonryColumns from '@/hooks/useMasonryColumns';
import type { SectionItem } from '@/types/sectionItem';
import { TEST_IDS } from './constants/testIds';
import { DEFAULT_COLUMNS_COUNT, GRID_ID, HORIZONTAL_GAP, VERTICAL_GAP } from '@/constants/grid';
import { PlaybackMode } from '@/types/media';
import MasonryGrid from '../MasonryGrid';
import Media from '../Media';
import { PULSE_NAMES } from '@/constants/pulseNames';
import useIsDesktop from '@/hooks/useIsDesktop';
import dynamic from 'next/dynamic';
import useStyles from './styles';

const CardTitle = dynamic(() => import('../CardTitle'));
const MediaOverlay = dynamic(() => import('../MediaOverlay'));

type CategoryGridProps = {
  id: number;
  items: SectionItem[];
  title: string;
  basePath: string;
};

const CategoryGrid = ({ title, items, id, basePath }: CategoryGridProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { columns, isReady } = useMasonryColumns({ columnsCount: DEFAULT_COLUMNS_COUNT });
  const isDesktop = useIsDesktop();
  const classes = useStyles();
  const headingId = `${id}-heading`;
  const dimensions = items.map(item => ({ width: item?.media?.width ?? 0, height: item?.media?.height ?? 0 }));

  return (
    <div ref={containerRef}>
      <MasonryGrid
        id={GRID_ID}
        columns={columns}
        dimensions={dimensions}
        aria-labelledby={headingId}
        verticalGap={VERTICAL_GAP}
        isVirtualizationEnabled={false}
        className={classNames(classes.grid, { [classes.gridReady]: isReady, [classes.gridHidden]: !isReady })}
        horizontalGap={HORIZONTAL_GAP}
        data-testid={`${title}-${TEST_IDS.MASONRY}`}
      >
        {items.map((item, itemIndex) => {
          const itemSlug = item?.slug ?? '/';
          const href = `/${basePath}/${itemSlug}`;
          const isPriority = itemIndex < 6;

          return (
            <Link
              href={href}
              prefetch
              key={item?.id}
              className={classNames('group', classes.link)}
              data-testid={`${title}-${TEST_IDS.LINK}`}
              id={item?.slug}
              data-pulse-name={item?.title ?? PULSE_NAMES.SECTION_ITEM}
              aria-label={`View ${item?.title ?? 'item'} details`}
            >
              <ErrorBoundary fallbackRender={() => null}>
                <div className={classes.mediaWrap}>
                  <Media item={item} priority={isPriority} mediaClassName={classes.mediaInner} playbackMode={PlaybackMode.Scheduled} />
                  {isDesktop ? <MediaOverlay title={item.title} /> : <CardTitle title={item.title} />}
                </div>
              </ErrorBoundary>
            </Link>
          );
        })}
      </MasonryGrid>
    </div>
  );
};

export default memo(CategoryGrid);
